import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { ChatRoom, SendMessageRequest } from 'amazon-ivs-chat-messaging';
import communityApi from '../../services/communityApi';
import streamApi from '../../services/streamApi';
import { authApi, readStoredToken } from '../../services/authApi';
import { readStoredUser } from '../../utils/navbarUser';
import VideoRequestButton from '../WatchPage/VideoRequestButton';
import { ROUTES } from '../../constants';
import './LiveStreamBlock.css';

const WATCH_HEARTBEAT_MS = 15000;

const PER_USER_CHATS_ENABLED =
  String(process.env.REACT_APP_ENABLE_PER_USER_CHATS).toLowerCase() === 'true';

const GIFT_TIERS = [
  { amount: 200, label: '200' },
  { amount: 600, label: '600' },
  { amount: 1000, label: '1K' },
  { amount: 2000, label: '2K' },
];

function getTwitchLogin(profile, liveStatus) {
  if (liveStatus?.platform === 'twitch' && liveStatus.platform_username) {
    return liveStatus.platform_username;
  }
  const platforms = profile?.connectedPlatforms || [];
  const twitch = platforms.find((p) => p.platform === 'twitch');
  if (twitch?.platformUsername) {
    return twitch.platformUsername;
  }
  if (String(profile?.oauthProvider || '').toLowerCase() === 'twitch') {
    const oauthUsername = String(profile?.oauthProviderUsername || '').trim();
    if (oauthUsername) return oauthUsername;
  }
  return null;
}

function LiveStreamBlock({ profile, liveStatus }) {
  const navigate = useNavigate();
  const twitchLogin = getTwitchLogin(profile, liveStatus);
  const parentHost = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  const streamTitle = `${profile?.username || 'Streamer'} is live`;
  const serverViewerCount = liveStatus?.live_status?.viewer_count ?? liveStatus?.viewer_count ?? 0;
  const [liveViewerCount, setLiveViewerCount] = useState(serverViewerCount);
  const [viewerLocalStream, setViewerLocalStream] = useState(null);

  const currentUser = readStoredUser();
  const isOwnStream = currentUser?.username && currentUser.username === profile?.username;
  const liveDestination = isOwnStream
    ? ROUTES.GO_LIVE
    : ROUTES.WATCH.replace(':username', encodeURIComponent(profile?.username || ''));
  const liveActionLabel = isOwnStream ? 'Manage Live' : 'Watch Live';

  useEffect(() => {
    setLiveViewerCount(serverViewerCount);
  }, [profile?.id]);

  useEffect(() => {
    const streamerId = profile?.id;
    if (!streamerId) return undefined;

    let cancelled = false;

    const ping = async () => {
      try {
        const res = await streamApi.sendWatchHeartbeat(streamerId);
        if (!cancelled && res && typeof res.viewer_count === 'number') {
          setLiveViewerCount(res.viewer_count);
        }
      } catch (err) {
        console.error('LiveStreamBlock heartbeat failed:', err);
      }
    };

    ping();
    const intervalId = setInterval(ping, WATCH_HEARTBEAT_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [profile?.id]);

  return (
    <section className="live-block" aria-label="Live stream">
      <header className="live-block__header">
        <span className="live-block__badge">LIVE</span>
        <h2 className="live-block__title">{streamTitle}</h2>
        <span className="live-block__viewers">{liveViewerCount} watching</span>
        <button
          type="button"
          className="live-block__action-btn"
          onClick={() => {
            if (profile?.username) {
              navigate(liveDestination);
            }
          }}
        >
          {liveActionLabel}
        </button>
      </header>

      <div className="live-block__grid">
        <div className="live-block__player">
          {twitchLogin ? (
            <iframe
              title={`${twitchLogin} live stream`}
              src={`https://player.twitch.tv/?channel=${encodeURIComponent(twitchLogin)}&parent=${encodeURIComponent(parentHost)}&autoplay=true&muted=true`}
              allowFullScreen
              frameBorder="0"
              scrolling="no"
            />
          ) : (
            <div className="live-block__player-missing">
              <p>No Twitch channel connected for this streamer yet.</p>
            </div>
          )}

          {viewerLocalStream && (
            <div className="live-block__viewer-grid">
              <ViewerTile stream={viewerLocalStream} label="YOU" />
            </div>
          )}
        </div>

        <aside className="live-block__sidebar">
          {PER_USER_CHATS_ENABLED && profile?.username ? (
            <LiveChatPanel
              roomId={`user:${profile.username}`}
              streamerUsername={profile.username}
            />
          ) : (
            <div className="live-block__chat-disabled">Community chat is unavailable.</div>
          )}
          <GiftSpadesPanel
            streamerId={profile?.id}
            streamerUsername={profile?.username}
          />
          {!isOwnStream && readStoredToken() && (
            <div className="live-block__video-request-panel">
              <div className="live-block__video-request-header">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="23 7 16 12 23 17 23 7" />
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                </svg>
                Request Live Video
              </div>
              <VideoRequestButton
                streamerId={profile?.id}
                streamerUsername={profile?.username}
                onLocalStreamChange={setViewerLocalStream}
              />
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}

LiveStreamBlock.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.string,
    username: PropTypes.string,
    connectedPlatforms: PropTypes.array,
  }).isRequired,
  liveStatus: PropTypes.object.isRequired,
};

function ViewerTile({ stream, label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream;
  }, [stream]);
  return (
    <div className="live-block__viewer-tile">
      <video ref={ref} autoPlay muted playsInline />
      <span className="live-block__viewer-tile-badge">{label}</span>
    </div>
  );
}

function LiveChatPanel({ roomId, streamerUsername }) {
  const storedUser = useMemo(() => readStoredUser() || {}, []);
  const currentUser = useMemo(
    () => ({
      id: storedUser.id || '',
      username: storedUser.username || '',
      avatar: storedUser.profileImage || '',
    }),
    [storedUser],
  );

  const [messages, setMessages] = useState([]);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [chatError, setChatError] = useState('');
  const [draft, setDraft] = useState('');

  const roomRef = useRef(null);
  const displayNameRef = useRef(currentUser.username);
  const profileImageRef = useRef(currentUser.avatar);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  useEffect(() => {
    if (!roomId) return undefined;

    let cancelled = false;
    let room = null;
    const cleanups = [];

    const tokenProvider = async () => {
      const resp = await communityApi.issueChatToken(roomId);
      if (resp.displayName) displayNameRef.current = resp.displayName;
      if (resp.profileImage) profileImageRef.current = resp.profileImage;
      return {
        token: resp.token,
        sessionExpirationTime: resp.sessionExpirationTime
          ? new Date(resp.sessionExpirationTime)
          : undefined,
        tokenExpirationTime: resp.tokenExpirationTime
          ? new Date(resp.tokenExpirationTime)
          : undefined,
      };
    };

    (async () => {
      try {
        const first = await communityApi.issueChatToken(roomId);
        if (cancelled) return;
        if (first.displayName) displayNameRef.current = first.displayName;
        if (first.profileImage) profileImageRef.current = first.profileImage;

        room = new ChatRoom({
          regionOrUrl: first.region || 'us-east-1',
          tokenProvider,
        });
        roomRef.current = room;

        cleanups.push(room.addListener('connecting', () => setConnectionState('connecting')));
        cleanups.push(
          room.addListener('connect', () => {
            setConnectionState('connected');
            setChatError('');
            communityApi
              .fetchHistory(roomId, { limit: 30 })
              .then((resp) => {
                if (cancelled) return;
                const historical = (resp?.messages || []).map((h) => ({
                  id: h.id,
                  content: h.content,
                  sendTime: h.sentAt ? new Date(h.sentAt) : undefined,
                  sender: { userId: h.senderUserId || undefined },
                  attributes: {
                    displayName: h.senderDisplayName || undefined,
                    profileImage: h.senderProfileImage || undefined,
                  },
                }));
                setMessages((prev) => {
                  const seen = new Set(prev.map((m) => m.id));
                  return [...historical.filter((h) => !seen.has(h.id)), ...prev];
                });
              })
              .catch(() => {});
          }),
        );
        cleanups.push(
          room.addListener('disconnect', (reason) => {
            setConnectionState('disconnected');
            if (reason !== 'clientDisconnect') {
              setChatError('Connection lost. Reconnecting…');
            }
          }),
        );
        cleanups.push(
          room.addListener('message', (message) => {
            setMessages((prev) => [...prev, message]);
          }),
        );
        cleanups.push(
          room.addListener('messageDelete', (event) => {
            setMessages((prev) => prev.filter((m) => m.id !== event.messageId));
          }),
        );

        room.connect();
      } catch (err) {
        if (!cancelled) setChatError(err?.message || 'Unable to connect to chat.');
      }
    })();

    return () => {
      cancelled = true;
      cleanups.forEach((off) => {
        try { off(); } catch {}
      });
      if (room) {
        try { room.disconnect(); } catch {}
      }
      roomRef.current = null;
    };
  }, [roomId]);

  const handleSend = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || !roomRef.current) return;
    try {
      const attributes = { displayName: displayNameRef.current || 'User' };
      if (profileImageRef.current) attributes.profileImage = profileImageRef.current;
      await roomRef.current.sendMessage(new SendMessageRequest(trimmed, attributes));
    } catch (err) {
      setChatError(err?.errorMessage || err?.message || 'Failed to send message.');
    }
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!draft.trim()) return;
    handleSend(draft);
    setDraft('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  const statusLabel = connectionState === 'connected'
    ? 'Connected'
    : connectionState === 'connecting' ? 'Connecting…' : 'Disconnected';

  return (
    <div className="live-chat" aria-label={`@${streamerUsername} chat`}>
      <div className="live-chat__header">
        <span className="live-chat__title">Community Chat</span>
        <span className={`live-chat__status live-chat__status--${connectionState}`}>
          {statusLabel}
        </span>
      </div>

      <div ref={scrollRef} className="live-chat__messages">
        {chatError && <div className="live-chat__error">{chatError}</div>}
        {!chatError && messages.length === 0 && (
          <div className="live-chat__empty">No messages yet. Say hi to {streamerUsername}.</div>
        )}
        {messages.map((m) => {
          const isMine = m.sender?.userId === currentUser.id;
          const displayName = m.attributes?.displayName
            || (isMine ? currentUser.username : m.sender?.userId)
            || 'unknown';
          const profileImage = m.attributes?.profileImage
            || (isMine ? currentUser.avatar : '')
            || '';
          return (
            <div key={m.id} className={`live-chat__msg${isMine ? ' live-chat__msg--mine' : ''}`}>
              <div className="live-chat__msg-avatar" aria-hidden="true">
                {profileImage ? (
                  <img src={profileImage} alt="" referrerPolicy="no-referrer" />
                ) : (
                  <span className="live-chat__msg-avatar-fallback">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="live-chat__msg-body">
                <span className="live-chat__msg-name">{displayName}:</span>
                <span className="live-chat__msg-content">{m.content}</span>
              </div>
            </div>
          );
        })}
      </div>

      <form className="live-chat__composer" onSubmit={handleSubmit}>
        <input
          type="text"
          className="live-chat__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={connectionState === 'connected' ? 'Message' : 'Connecting…'}
          disabled={connectionState !== 'connected'}
        />
        <button
          type="submit"
          className="live-chat__send"
          disabled={connectionState !== 'connected' || !draft.trim()}
        >
          Send
        </button>
      </form>
    </div>
  );
}

LiveChatPanel.propTypes = {
  roomId: PropTypes.string.isRequired,
  streamerUsername: PropTypes.string.isRequired,
};

function GiftSpadesPanel({ streamerId, streamerUsername }) {
  const [selectedTier, setSelectedTier] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [toast, setToast] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleGift = async () => {
    const amount = selectedTier || Number(customAmount) || 0;
    if (!amount || !streamerId || submitting) return;
    setSubmitting(true);
    try {
      const result = await authApi.giftSpades({ streamerId, amount });
      setToast(`Sent ${result.spades_sent} spades to @${streamerUsername}!`);
      setSelectedTier(null);
      setCustomAmount('');
    } catch (err) {
      setToast(err?.message || 'Failed to send spades');
    } finally {
      setSubmitting(false);
      setTimeout(() => setToast(''), 3000);
    }
  };

  const disabled = submitting || !streamerId || (!selectedTier && !Number(customAmount));

  return (
    <div className="gift-spades" aria-label="Send spades">
      <div className="gift-spades__header">
        <span className="gift-spades__title">Send Spades</span>
      </div>
      <div className="gift-spades__tiers">
        {GIFT_TIERS.map((tier) => (
          <button
            key={tier.amount}
            type="button"
            className={`gift-spades__tier${selectedTier === tier.amount ? ' gift-spades__tier--active' : ''}`}
            onClick={() => {
              setSelectedTier(tier.amount);
              setCustomAmount('');
            }}
            disabled={submitting}
          >
            ♠ {tier.label}
          </button>
        ))}
      </div>
      <input
        type="number"
        min="1"
        className="gift-spades__custom"
        placeholder="Custom amount"
        value={customAmount}
        onChange={(e) => {
          setCustomAmount(e.target.value);
          setSelectedTier(null);
        }}
        disabled={submitting}
      />
      <button
        type="button"
        className="gift-spades__submit"
        onClick={handleGift}
        disabled={disabled}
      >
        {submitting ? 'Sending…' : 'Send'}
      </button>
      {toast && <div className="gift-spades__toast">{toast}</div>}
    </div>
  );
}

GiftSpadesPanel.propTypes = {
  streamerId: PropTypes.string,
  streamerUsername: PropTypes.string,
};

export default LiveStreamBlock;
